import {Component} from '@angular/core';
import {QueryService} from '@pt/api-client';
import {AbstractWidgetDirective} from '../../abstract-widget.directive';
import {ActionsSubject, Store} from '@ngrx/store';
import {UiWidget, WidgetConfiguration} from '@pt/pt-models';
import {TranslateService} from '@ngx-translate/core';
import {WidgetDialogService} from '../../../services/widget-dialog.service';
import {WidgetDpiaV0Header, WidgetDpiaV0Preview} from './models/widget-dpia-v0-preview';
import {WidgetDpiaV0Value} from './models/widget-dpia-v0-value';
import {forkJoin} from 'rxjs';
import {WidgetUtils} from '../../widget.utils';
import {map} from 'rxjs/operators';
import {
  WidgetDpiaV0QueryResponse,
  WidgetDpiaV0QueryResponseDemage,
  WidgetDpiaV0QueryResponseThreat
} from './models/widget-dpia-v0-query-response';
import {WidgetDpiaV0Data} from './models/widget-dpia-v0-data';
import {NgxPermissionsService} from 'ngx-permissions';

@Component({
  selector: 'pt-widget-dpia-v0',
  template: `
    <div class='position-relative' *ngIf="internalStatus.enabled">
      <pt-widget-disabled
        *ngIf='internalStatus.isDisabled'
        [showLoader]="internalStatus.viewMode === 'LOADING'">
      </pt-widget-disabled>

      <pt-widget-header
        class='w-100'
        [label]='widget?.label'
        [help]='widget?.help'

        [comment]="comment"
        [unsavedComment]="unsavedComment"
        [showCancelButton]='internalStatus.showCancelButton'
        [showCommentButton]='internalStatus.showCommentButton'
        [showEditButton]='internalStatus.showEditButton'
        [showReloadButton]='internalStatus.showReloadButton'
        [showSaveButton]='internalStatus.showSaveButton'
        [showOptButton]='internalStatus.showOptButton'

        [disabledCancelButton]='internalStatus.disabledCancelButton'
        [disabledCommentButton]='internalStatus.disabledCommentButton'
        [disabledEditButton]='internalStatus.disabledEditButton'
        [disabledOptButton]='internalStatus.disabledOptButton'
        [disabledReloadButton]='internalStatus.disabledReloadButton'
        [disabledSaveButton]='internalStatus.disabledSaveButton'
        [loading]='internalStatus.isLoading'

        (clickSaveWidget)='saveWidget()'
        (clickResetWidget)='resetWidget(widget)'
        (clickEditWidget)='dispatchEditState()'
        (clickReloadWidget)='reloadWidget(widget)'
        (clickCreateOpt)='createOptWidget()'
        (clickCommentWidget)='clickCommentWidget()'>
      </pt-widget-header>

      <ng-container *ngIf='!internalStatus.showOptButton'>

        <pt-widget-dpia-v0-edit
          [show]="internalStatus.viewMode === 'EDIT' || internalStatus.viewMode === 'LOADING' || internalStatus.viewMode === 'PREVIEW'"
          [treatmentRiskFactors]="treatmentRiskFactors"
          [editorData]='editorData'
          [preview]="internalStatus.viewMode === 'PREVIEW'"
          (changeValue)='changeCurrentValue($event)'>
        </pt-widget-dpia-v0-edit>

        <pt-widget-error
          *ngIf="internalStatus.viewMode === 'ERROR'"
          [errorMessage]='internalStatus.errorMessage | async'>
        </pt-widget-error>

        <pt-widget-footer>
        </pt-widget-footer>
      </ng-container>
    </div>
  `
})
export class WidgetDpiaV0Component extends AbstractWidgetDirective<WidgetDpiaV0Preview> {
  public editorData: WidgetDpiaV0Preview;
  public treatmentRiskFactors: number;
  // il suo valore è il risultato di un form che creo ogni volta che mi cambia il valore settando di conseguenza questa variabile
  formValid = false;

  // la funzione is valid viene utilizzata anche dalla classe astratta e dall'HTML.
  private widgetValue: WidgetDpiaV0Value;

  constructor(public store: Store,
              public actionsSubject: ActionsSubject,
              public translateService: TranslateService,
              public widgetDialogService: WidgetDialogService,
              public service: QueryService,
              public permissionService: NgxPermissionsService) {
    super(store, actionsSubject, translateService, widgetDialogService, permissionService);
  }

  loadWidget(widget: UiWidget): void {
    this.dispatchInitState();
    this.editorData = null;
    this.treatmentRiskFactors = widget?.args?.data?.treatmentRiskFactors || 0;

    const queryDamage = widget?.args?.queryDamage;
    // if (value?.damageType) {
    //   queryDamage.where = [{column: 'type', op: 'EQUAL', value: value.damageType}]
    // } else {
    //   // queryDamage.where = []
    // }
    forkJoin({
      demage: this.service.resolve(queryDamage).pipe(map(result => WidgetUtils.mappingQueryResponse(result, widget?.args?.queryDamage.mapping) as WidgetDpiaV0QueryResponseDemage[])),
      threat: this.service.resolve(widget?.args?.queryThreat).pipe(map(result => WidgetUtils.mappingQueryResponse(result, widget?.args?.queryThreat.mapping) as WidgetDpiaV0QueryResponseThreat[]))
    }).subscribe(
      (response: { demage: WidgetDpiaV0QueryResponseDemage[], threat: WidgetDpiaV0QueryResponseThreat[] }) => {

        const queryResponse: WidgetDpiaV0QueryResponse = {
          threat: response.threat,
          demage: response.demage
        };

        const data = widget?.args?.data as WidgetDpiaV0Data;
        const header = widget?.args?.header as WidgetDpiaV0Header;

        const value: WidgetDpiaV0Value = widget.value;
        this.widgetValue = value;
        this.editorData = {
          header,
          queryResponse,
          value,
          data
        };
        this.valueController(widget, this.editorData.queryResponse);
        this.checkValidity();
        this.dispatchCompleteState(this.editorData);
      }, () => {
        this.dispatchErrorState();
      }
    );
  }

  saveWidget(ignoreTrigger?: boolean): void {
    const widget = JSON.parse(JSON.stringify(this.widget)) as UiWidget;
    widget.value = this.widgetValue;
    widget.comment = this.comment;
    this.completeSaveWidget(widget, ignoreTrigger);
  }

  changeCurrentValue(value: WidgetDpiaV0Value) {
    if (JSON.stringify(this.widgetValue) !== JSON.stringify(value)) {
      this.widgetValue = value;
      this.checkValidity();

      if (this.internalStatus.isAutoSave && !this.internalStatus.isLoading && this.internalStatus.isDraft) { // && !this.internalStatus.isLoading) {
        this.saveWidget();
      }
    }
  }

  configuration(): WidgetConfiguration {
    return {supportAutoSave: true, supportValidation: false};
  }

  private checkValidity() {
    this.isValid$.next(!!this.widget.value?.damageType);
  }

  private valueController(widget: UiWidget, data: WidgetDpiaV0QueryResponse) {
    const noData = !data || data?.demage?.length === 0 || data?.threat?.length === 0;
    if (noData) {
      this.sendWidgetNotificationInvalidQueryResponseNoData(data);
    }
  }
}
